import React, { useEffect, useMemo, useState } from 'react';
import { createEntityLink } from '../services/intelligenceApi';
import { entityLabel } from '../config/entityRoutes';
import EntityPicker from './EntityPicker';

const RELATIONS = [
  { value: 'uses', label: 'uses' },
  { value: 'attributed_to', label: 'attributed to' },
  { value: 'related_to', label: 'related to' },
  { value: 'linked_to', label: 'linked to' },
  { value: 'observed_in', label: 'observed in' },
  { value: 'operated_by', label: 'operated by' },
  { value: 'supported_by', label: 'supported by' },
  { value: 'enriches', label: 'enriches' },
  { value: 'resolves_to', label: 'resolves to' },
  { value: 'located_at', label: 'located at' },
  { value: 'located_in', label: 'located in' },
];

const DEFAULT_TARGET_TYPES = ['indicator', 'actor', 'case', 'persona', 'evidence', 'search'];
const SUPPORT_TYPES = ['search', 'evidence', 'indicator'];

function defaultRelationFor(sourceType) {
  if (sourceType === 'actor') return 'uses';
  if (sourceType === 'indicator') return 'observed_in';
  if (sourceType === 'case') return 'linked_to';
  return 'related_to';
}

/**
 * OpenCTI-style composer:
 *   Actor Russ  --uses-->  Phone XXX
 *                 and phone --supported_by--> Search Y   (optional)
 */
function AddRelationshipForm({
  sourceType,
  sourceId,
  sourceLabel,
  allowedTargetTypes = DEFAULT_TARGET_TYPES,
  caseId,
  actorId,
  onCreated,
  disabled = false,
}) {
  const [target, setTarget] = useState(null);
  const [relation, setRelation] = useState(() => defaultRelationFor(sourceType));
  const [support, setSupport] = useState(null);
  const [includeSupport, setIncludeSupport] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setRelation(defaultRelationFor(sourceType));
  }, [sourceType]);

  const fromLabel = useMemo(
    () => sourceLabel || `${entityLabel(sourceType)} ${sourceId}`,
    [sourceLabel, sourceType, sourceId],
  );

  const preview = useMemo(() => {
    if (!target) return null;
    const lines = [
      `${fromLabel} ${relation.replace(/_/g, ' ')} ${target.label}`,
    ];
    if (includeSupport && support) {
      lines.push(`${target.label} supported by ${support.label}`);
    }
    return lines;
  }, [fromLabel, relation, target, includeSupport, support]);

  const createLinks = async (selectedTarget, selectedSupport, withSupport) => {
    if (disabled || saving || !sourceType || !sourceId || !selectedTarget?.id) return;
    if (withSupport && !selectedSupport) {
      setError('Pick a search or evidence item for "supported by", or turn that option off.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const primary = await createEntityLink({
        source_type: sourceType,
        source_id: sourceId,
        target_type: selectedTarget.type,
        target_id: selectedTarget.id,
        relation,
        label: selectedTarget.label,
      });

      if (withSupport && selectedSupport) {
        await createEntityLink({
          source_type: selectedTarget.type,
          source_id: selectedTarget.id,
          target_type: selectedSupport.type,
          target_id: selectedSupport.id,
          relation: 'supported_by',
          label: selectedSupport.label,
        });
      }

      setTarget(null);
      setSupport(null);
      setIncludeSupport(false);
      onCreated?.(primary.data.link || primary.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create relationship');
    } finally {
      setSaving(false);
    }
  };

  const handleTargetChange = (entity) => {
    if (entity?.justCreated) {
      const clean = { ...entity, justCreated: false };
      setTarget(clean);
      // Inline create already attached actor_id/case_id; also write the graph edge.
      createLinks(clean, null, false);
      return;
    }
    setTarget(entity);
  };

  const submit = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    await createLinks(target, support, includeSupport);
  };

  return (
    <form className="relationship-composer" onSubmit={submit}>
      <div className="relationship-composer-header">
        <h4>Create relationship</h4>
        <p>
          Example: <em>Russ Davis</em> uses <em>phone 555-0100</em>, supported by an enrichment search.
          Search existing indicators or create a new one inline.
        </p>
      </div>

      <div className="relationship-composer-flow">
        <div className="relationship-node relationship-node-from">
          <span className="relationship-node-label">From</span>
          <div className="relationship-node-card">
            <span className={`entity-badge entity-badge-${sourceType}`}>{entityLabel(sourceType)}</span>
            <strong>{fromLabel}</strong>
          </div>
        </div>

        <div className="relationship-edge">
          <label className="form-label">Relation</label>
          <select
            className="form-input"
            value={relation}
            onChange={(e) => setRelation(e.target.value)}
            disabled={disabled || saving}
          >
            {RELATIONS.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </div>

        <div className="relationship-node relationship-node-to">
          <span className="relationship-node-label">To (search existing or create new)</span>
          <EntityPicker
            value={target}
            onChange={handleTargetChange}
            allowedTypes={allowedTargetTypes}
            excludeType={sourceType}
            excludeId={sourceId}
            placeholder="Search phone, domain, wallet, indicator…"
            disabled={disabled || saving}
            allowCreateIndicator
            createCaseId={caseId || (sourceType === 'case' ? sourceId : undefined)}
            createActorId={actorId || (sourceType === 'actor' ? sourceId : undefined)}
          />
        </div>
      </div>

      <div className="relationship-support">
        <label className="relationship-support-toggle">
          <input
            type="checkbox"
            checked={includeSupport}
            onChange={(e) => {
              setIncludeSupport(e.target.checked);
              if (!e.target.checked) setSupport(null);
            }}
            disabled={disabled || saving || !target}
          />
          Also mark that target as <strong>supported by</strong> a search / evidence
        </label>
        {includeSupport && (
          <EntityPicker
            value={support}
            onChange={setSupport}
            allowedTypes={SUPPORT_TYPES}
            excludeType={target?.type}
            excludeId={target?.id}
            placeholder="Search enrichment runs or evidence…"
            disabled={disabled || saving || !target}
          />
        )}
      </div>

      {preview && (
        <div className="relationship-preview">
          {preview.map((line) => (
            <div key={line}>{line}</div>
          ))}
        </div>
      )}

      {error && <p className="relationship-composer-error">{error}</p>}

      <div className="relationship-composer-actions">
        <button type="submit" className="btn btn-primary" disabled={disabled || saving || !target}>
          {saving ? 'Creating…' : 'Create relationship'}
        </button>
      </div>
    </form>
  );
}

export default AddRelationshipForm;
